import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Robot, GraphIcon, Plus, PencilSimple, MagnifyingGlass, Gear, House, ChatCircle, Warning,
} from "@phosphor-icons/react";

export default function CompanyChatbot() {
  const [settings, setSettings] = useState(null);
  const [flows, setFlows] = useState([]);
  const [q, setQ] = useState("");
  const [openInact, setOpenInact] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [sectors, setSectors] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [s, f, sec] = await Promise.all([
        api.get("/chatbot/settings"),
        api.get("/chatbot/flows"),
        api.get("/sectors").catch(() => ({ data: [] })),
      ]);
      setSettings(s.data);
      setFlows(f.data);
      setSectors(sec.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    try {
      const { data } = await api.put("/chatbot/settings", { enabled: !settings.enabled });
      setSettings(data);
      toast.success(data.enabled ? "Chatbot ativado" : "Chatbot desativado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createFlow = async (identifier) => {
    try {
      const isFirst = flows.length === 0;
      const { data } = await api.post("/chatbot/flows", {
        identifier: identifier.trim() || "Novo menu",
        is_root: isFirst,
        message: { type: "text", text: "" },
        format: "options",
        options: [],
      });
      setFlows((prev) => [...prev, data]);
      setOpenNew(false);
      toast.success("Menu criado");
      navigate("/chatbot/diagram");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = flows.filter((f) => !q || f.identifier.toLowerCase().includes(q.toLowerCase()));

  if (!settings) return <div className="p-8 text-sm text-[#5C5C59]">Carregando…</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="chatbot-page">
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Automação</p>
        <h1 className="text-4xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <Robot size={32} weight="duotone" /> Chatbot
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          O chatbot realiza pré-atendimentos automaticamente. Após o processo, direciona a um departamento ou finaliza o atendimento.
        </p>
      </div>

      {/* Ativar */}
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5 mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black flex items-center gap-2"><Gear size={18} weight="duotone" /> Ativar Chatbot</h2>
          <p className="text-xs text-[#5C5C59] mt-1">Ativar Chatbot para atendimento automático.</p>
        </div>
        <Switch checked={settings.enabled} onCheckedChange={toggleEnabled} data-testid="chatbot-enable-switch" />
      </section>

      {/* Inatividade */}
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5 mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black flex items-center gap-2"><Warning size={18} weight="duotone" /> Ação de Inatividade</h2>
          <p className="text-xs text-[#5C5C59] mt-1">Defina uma ação do chatbot para casos de inatividade.</p>
        </div>
        <Button onClick={() => setOpenInact(true)} data-testid="config-inactivity-btn" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">
          <Gear size={14} weight="bold" className="mr-2" /> Configurar
        </Button>
      </section>

      {/* Diagrama */}
      <section className="bg-white border border-[#E5E5E0] shadow-brutal p-5 mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black flex items-center gap-2"><GraphIcon size={18} weight="duotone" /> Diagrama</h2>
          <p className="text-xs text-[#5C5C59] mt-1">Visualize o fluxo completo do chatbot em formato de diagrama interativo.</p>
        </div>
        <Button onClick={() => navigate("/chatbot/diagram")} data-testid="view-diagram-btn" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">
          <GraphIcon size={14} weight="bold" className="mr-2" /> Visualizar
        </Button>
      </section>

      {/* Menu list */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C59]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar…" className="rounded-none pl-9" data-testid="chatbot-search" />
        </div>
        <Button onClick={() => setOpenNew(true)} data-testid="add-flow-btn" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">
          <Plus size={14} weight="bold" className="mr-1" /> Adicionar
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="flows-list">
        <div className="grid grid-cols-[2fr_1fr_1fr] px-4 py-2 border-b border-[#E5E5E0] text-[10px] uppercase tracking-wider font-bold text-[#5C5C59]">
          <span>Identificador</span>
          <span className="text-center">Menu inicial</span>
          <span className="text-right">Ação</span>
        </div>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-[#5C5C59]">
            Nenhum menu ainda. Clique em <b>Adicionar</b> ou <b>Visualizar</b> pra montar seu primeiro fluxo.
          </div>
        )}
        {filtered.map((f) => (
          <div key={f.id} className="grid grid-cols-[2fr_1fr_1fr] px-4 py-3 border-b border-[#F0F0EC] items-center text-sm" data-testid={`flow-${f.id}`}>
            <span className="font-semibold">{f.identifier}</span>
            <span className="text-center">
              {f.is_root && <House size={20} weight="fill" className="text-[#0F4C3A] mx-auto" />}
              {!f.is_root && <ChatCircle size={16} className="text-[#5C5C59] mx-auto" />}
            </span>
            <span className="text-right">
              <button onClick={() => navigate(`/chatbot/diagram?flow=${f.id}`)} className="text-[#0F4C3A] font-semibold text-xs hover:underline">
                Editar ›
              </button>
            </span>
          </div>
        ))}
      </div>

      {/* New flow modal */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-white rounded-none max-w-md">
          <DialogHeader><DialogTitle>Novo menu</DialogTitle></DialogHeader>
          <NewFlowForm onSubmit={createFlow} />
        </DialogContent>
      </Dialog>

      {/* Inactivity modal */}
      {openInact && (
        <InactivityDialog
          settings={settings}
          sectors={sectors}
          onClose={() => setOpenInact(false)}
          onSaved={(s) => { setSettings(s); setOpenInact(false); }}
        />
      )}
    </div>
  );
}

function NewFlowForm({ onSubmit }) {
  const [name, setName] = useState("");
  return (
    <>
      <div>
        <Label className="text-xs uppercase font-semibold tracking-wider">Identificador do menu</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-1" placeholder="Ex.: Menu principal" autoFocus data-testid="new-flow-name" />
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(name)} disabled={!name.trim()} data-testid="new-flow-submit" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">
          Criar
        </Button>
      </DialogFooter>
    </>
  );
}

function InactivityDialog({ settings, sectors, onClose, onSaved }) {
  const [minutes, setMinutes] = useState(settings.inactivity_minutes ?? 15);
  const [action, setAction] = useState(settings.inactivity_action?.type || "none");
  const [sectorId, setSectorId] = useState(settings.inactivity_action?.sector_id || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        inactivity_minutes: Number(minutes),
        inactivity_action: action === "transfer_department" ? { type: action, sector_id: sectorId } : { type: action },
      };
      const { data } = await api.put("/chatbot/settings", payload);
      toast.success("Salvo");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-none max-w-md" data-testid="inactivity-dialog">
        <DialogHeader><DialogTitle>Ação de inatividade</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Minutos sem resposta do cliente</Label>
            <Input type="number" min="1" max="1440" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="rounded-none mt-1 font-mono" data-testid="inactivity-minutes" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">O que fazer?</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="rounded-none mt-1" data-testid="inactivity-action-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                <SelectItem value="none">Não fazer nada (deixar bot em pausa)</SelectItem>
                <SelectItem value="end_chat">Encerrar atendimento</SelectItem>
                <SelectItem value="transfer_department">Transferir para departamento</SelectItem>
                <SelectItem value="restart">Reiniciar chatbot no menu inicial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {action === "transfer_department" && (
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Departamento</Label>
              <Select value={sectorId} onValueChange={setSectorId}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Escolha…" /></SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="inactivity-save" className="rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
